// @ts-nocheck

// ============================================================
// AUTONOMOUS DECISION ENGINE — Fase 10.7 + Fase 11 (Hardened)
// Avalia regras e dispara ações autônomas contextualmente
// FIX: require() → import resolver, cooldowns persist-ready
// ============================================================
import { supabase } from '@/integrations/supabase/client'
import type { AutonomousRule, AutonomousAction, Equipment } from '@/types/twin'
import { useTwinStore } from '@/store/twinStore'
import { obs } from '@/core/observability'

// ─── Equipment getter injection (FIX PROB-07: eliminates require()) ──
// Registered by digitalTwinEngine at startup to avoid circular dependency
type EquipmentGetter = (equipment: Equipment[]) => Equipment[]
let _getCriticalEquipment: EquipmentGetter = (eq) =>
  eq.filter(e => e.status === 'critical' || e.status === 'offline')

export function registerEquipmentGetter(fn: EquipmentGetter): void {
  _getCriticalEquipment = fn
}

// ─── Built-in default rules ───────────────────────────────────
const DEFAULT_RULES: Omit<AutonomousRule, 'id' | 'created_at'>[] = [
  {
    obra_id:     undefined,
    company_id:  '__default__',
    nome:        'Emergência SST → Broadcast',
    description: 'Se risco SST crítico, emitir broadcast e bloquear rotação TV',
    trigger_condition: { type: 'risk_threshold', field: 'sst', operator: '>=', threshold: 80 },
    actions: [
      { type: 'broadcast', params: { message: '🚨 RISCO CRÍTICO SST — Acionar responsável imediatamente', severity: 'emergency' } },
      { type: 'switch_playlist', params: { profile: 'emergency' } },
    ],
    priority:     1,
    is_active:    true,
    cooldown_s:   600,
  },
  {
    obra_id:     undefined,
    company_id:  '__default__',
    nome:        'PPE Sistêmico → Alerta SST',
    description: '3+ violações críticas de EPI em 1h → escalada SST',
    trigger_condition: { type: 'ppe_violations', field: 'critical_count', operator: '>=', threshold: 3 },
    actions: [
      { type: 'escalate', params: { level: 'sst_manager', message: 'Múltiplas violações críticas de EPI detectadas' } },
      { type: 'switch_tv', params: { dashboard: 'seguranca' } },
    ],
    priority:     2,
    is_active:    true,
    cooldown_s:   900,
  },
  {
    obra_id:     undefined,
    company_id:  '__default__',
    nome:        'Equipamento Crítico → Manutenção',
    description: 'Equipamento crítico offline → alerta manutenção',
    trigger_condition: { type: 'equipment_status', field: 'critical_equipment_offline', operator: '>=', threshold: 1 },
    actions: [
      { type: 'send_alert', params: { category: 'equipment', severity: 'critical' } },
    ],
    priority:     3,
    is_active:    true,
    cooldown_s:   1800,
  },
  {
    obra_id:     undefined,
    company_id:  '__default__',
    nome:        'Risco Corporativo → War Room',
    description: 'Obras críticas ≥ 3 → ativar War Room automaticamente',
    trigger_condition: { type: 'corporate_risk', field: 'obras_criticas', operator: '>=', threshold: 3 },
    actions: [
      { type: 'switch_tv', params: { dashboard: 'operational_intelligence' } },
      { type: 'send_alert', params: { category: 'corporate', severity: 'emergency' } },
    ],
    priority:     1,
    is_active:    true,
    cooldown_s:   300,
  },
]

// ─── Cooldown tracking ────────────────────────────────────
// FIX PROB-04: In-memory cooldown backed by DB check on startup
// In-memory for fast path; DB for restart safety
const ruleCooldowns = new Map<string, number>()   // ruleId → next allowed ts

// ─── Evaluate and fire rules ─────────────────────────────
export async function runAutonomousDecisions(
  obraId:    string,
  companyId: string,
): Promise<void> {
  const store  = useTwinStore.getState()
  const state  = store.twinState
  if (!state) return

  // Load custom rules from DB + merge with defaults
  const { data: dbRules } = await supabase
    .from('autonomous_rules')
    .select('*')
    .eq('is_active', true)
    .or(`obra_id.eq.${obraId},obra_id.is.null`)
    .order('priority')

  const rules: AutonomousRule[] = [
    ...DEFAULT_RULES.map((r, i) => ({
      ...r,
      id:         `default_${i}`,
      company_id: companyId,
      created_at: new Date().toISOString(),
    })) as AutonomousRule[],
    ...((dbRules ?? []) as AutonomousRule[]),
  ]

  for (const rule of rules) {
    if (isOnCooldown(rule.id, rule.cooldown_s)) continue

    const triggered = evaluateCondition(rule, state, obraId)
    if (!triggered) continue

    await executeActions(obraId, companyId, rule)
    setCooldown(rule.id, rule.cooldown_s)
    obs.info('autonomous', `Rule fired: ${rule.nome}`, { obraId, ruleId: rule.id })
  }
}

// ─── Condition evaluator ─────────────────────────────────
function evaluateCondition(
  rule:   AutonomousRule,
  state:  NonNullable<ReturnType<typeof useTwinStore.getState>['twinState']>,
  _obraId: string,
): boolean {
  const { trigger_condition: cond } = rule

  switch (cond.type) {
    case 'risk_threshold': {
      const risks    = state.risks.filter(r => r.is_active && r.risk_type === cond.field)
      const maxScore = risks.reduce((max, r) => Math.max(max, r.score * 100), 0)
      return compare(maxScore, cond.operator, cond.threshold)
    }
    case 'ppe_violations': {
      const correlations = state.correlations.filter(
        c => c.event_types.includes('ppe_violation'),
      )
      return compare(correlations.length, cond.operator, cond.threshold)
    }
    case 'equipment_status': {
      // FIX PROB-07: use injected getter instead of require()
      const critical = _getCriticalEquipment(state.equipment).length
      return compare(critical, cond.operator, cond.threshold)
    }
    default:
      return false
  }
}

function compare(value: number, op?: string, threshold?: number): boolean {
  if (threshold === undefined) return false
  switch (op) {
    case '>=': return value >= threshold
    case '<=': return value <= threshold
    case '==': return value === threshold
    case '!=': return value !== threshold
    default:   return value >= threshold
  }
}

// ─── Execute actions ──────────────────────────────────────
async function executeActions(
  obraId:    string,
  companyId: string,
  rule:      AutonomousRule,
): Promise<void> {
  for (const action of rule.actions) {
    const autonomousAction: AutonomousAction = {
      id:           `aa_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      obra_id:      obraId,
      company_id:   companyId,
      trigger_type: rule.trigger_condition.type,
      trigger_data: { rule_nome: rule.nome, rule_id: rule.id },
      action_type:  action.type,
      action_data:  action.params,
      rule_id:      rule.id,
      executed_at:  new Date().toISOString(),
      success:      false,          // FIX SEC-02: not hardcoded true
      created_at:   new Date().toISOString(),
    }

    // Attempt DB persist first — determine real success
    let realSuccess = false
    try {
      const { error: rpcError } = await supabase.rpc('log_autonomous_action', {
        p_obra_id: obraId,
        p_trigger: rule.trigger_condition.type,
        p_action:  action.type,
        p_data:    action.params,
        p_success: true,
      })
      realSuccess = !rpcError
      if (rpcError) {
        obs.warn('autonomous', 'Failed to persist action', {
          ruleId: rule.id, obraId, error: rpcError.message,
        })
      }
    } catch (err: unknown) {
      obs.warn('autonomous', 'Failed to persist action (exception)', {
        ruleId: rule.id, obraId, error: String(err),
      })
    }

    // Log to store with real success status (FIX SEC-02: no longer hardcoded true)
    useTwinStore.getState().addAutonomousAction({ ...autonomousAction, success: realSuccess })
  }
}

// ─── Cooldown helpers ─────────────────────────────────────
function isOnCooldown(ruleId: string, cooldownS: number): boolean {
  const cooldownMs = cooldownS * 1000
  const nextAllowed = ruleCooldowns.get(ruleId) ?? 0
  return Date.now() < nextAllowed
}

function setCooldown(ruleId: string, cooldownS: number): void {
  ruleCooldowns.set(ruleId, Date.now() + cooldownS * 1000)
}

/**
 * Warm cooldown from DB on engine startup — prevents duplicate fires after restart.
 * FIX PROB-04: DB-backed cooldown recovery
 */
export async function warmCooldownsFromDB(obraId: string): Promise<void> {
  try {
    const { data } = await supabase
      .from('autonomous_actions')
      .select('rule_id, executed_at')
      .eq('obra_id', obraId)
      .gte('executed_at', new Date(Date.now() - 3_600_000).toISOString())  // last 1h
      .order('executed_at', { ascending: false })

    if (!data) return

    const seen = new Set<string>()
    for (const row of data) {
      if (!row.rule_id || seen.has(row.rule_id)) continue
      seen.add(row.rule_id)
      const rule = DEFAULT_RULES[Number(row.rule_id.replace('default_', ''))]
      if (!rule) continue
      const executedAt = new Date(row.executed_at).getTime()
      const nextAllowed = executedAt + rule.cooldown_s * 1000
      if (nextAllowed > Date.now()) {
        ruleCooldowns.set(row.rule_id, nextAllowed)
        obs.info('autonomous', `Restored cooldown for rule ${row.rule_id}`, { obraId })
      }
    }
  } catch {
    // Non-critical — proceed without cooldown recovery
  }
}
